import searchConfig from '@/config/search';
import { ca } from '@/lib/channelAttribution.js';
import { cookies } from '@/lib/cookies.js';
import { getConsent } from '@/lib/trustarc.js';
import { useCurrencyStore } from '@/stores/currency';

/**
 * COD-877: builds the 3PI partner `q=` token (BASE64 JSON) for 3PI property clicks.
 * Copied from PWA (pwa/src/lib/threePIQueryParams.js) with staticweb sources; must stay in sync
 * with the partner post MVP sheet (all params where "Passed from Web To WL = y").
 * Excluded on web by spec: firstname, lastname, email (PII) and att.
 * Client-only: reads cookies and channel attribution, so call it after mount.
 */

const TRACKING_DEFAULT = '-1';

function generateRoomSplit(numGuests) {
  if (numGuests <= 0) {
    return 0;
  }
  if (numGuests <= 2) {
    return 1;
  }
  if (numGuests <= 8) {
    return 2;
  }
  // 9+ guests: one room per 4 guests, capped at 8 rooms (same table as the PWA implementation).
  return Math.min(8, Math.ceil(numGuests / 4));
}

function divideEvenly(numGuests, numRooms) {
  if (numRooms <= 0) {
    return [];
  }

  const baseGuestsPerRoom = Math.floor(numGuests / numRooms);
  const extraGuests = numGuests % numRooms;

  return Array.from({ length: numRooms }, (unused, roomIndex) =>
    roomIndex < extraGuests ? baseGuestsPerRoom + 1 : baseGuestsPerRoom);
}

function formatRoomPax(numGuests) {
  if (numGuests <= 0) {
    return '0';
  }
  // 1-3 guests: one bed per room so dorm min prices are returned on the SRP (PWA parity).
  if (numGuests <= 3) {
    return Array.from({ length: numGuests }).fill('1').join('_');
  }
  return divideEvenly(numGuests, generateRoomSplit(numGuests)).map(String).join('_');
}

// btoa only handles latin1, so the JSON is UTF-8 escaped first (runs client-side after mount).
function toBase64(jsonString) {
  return btoa(
    encodeURIComponent(jsonString).replace(/%([0-9A-F]{2})/g, (match, hexPair) =>
      String.fromCharCode(Number.parseInt(hexPair, 16))),
  );
}

export function useThreePiQueryParams() {
  const { $i18n } = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();
  const dateTime = useDateTime();
  const searchStore = useSearchStore();
  const currencyStore = useCurrencyStore();
  const { isMobile } = useDevice();
  const { hasLoggedInUser } = useAccount();
  const { useFormatter } = useFormatters();

  const encodeThreePiParameters = (property, locationInfo) => {
    // The store seeds EMPTY_DATE ({ year: 0, ... }) until a search form initialises it, and
    // formatToFlat would silently emit 1899-11-30 — fall back to the search form defaults.
    const storedCheckIn = searchStore.getSearchCheckIn;
    const storedCheckOut = searchStore.getSearchCheckOut;
    const checkIn = dateTime.isValidDate(storedCheckIn) ? storedCheckIn : dateTime.tomorrow;
    const checkOut = dateTime.isValidDate(storedCheckOut)
      ? storedCheckOut
      : dateTime.addDaysToDate(checkIn, searchConfig.defaultNights);
    const guests = searchStore.getSearchNumberOfGuests || 1;
    const channelAttribution = ca?.getLastChannelAttribution() || {};
    const {
      loadFunctional,
      loadAdvertising,
    } = getConsent(true ? document?.cookie : '');
    const localeObject = useFormatter.getLocaleObjectByCode($i18n?.locale?.value);

    const paramDict = {
      checkin: dateTime.formatToFlat(checkIn),
      checkout: dateTime.formatToFlat(checkOut),
      name: property?.name || '',
      roompax: formatRoomPax(guests),
      segmentanonid: cookies.getCookie('ajs_anonymous_id') || TRACKING_DEFAULT,
      loggedin: !!toValue(hasLoggedInUser),
      currency: currencyStore.getCurrentCurrency?.code || 'EUR',
      language: localeObject?.isoUnder || 'en_GB',
      // @devNote: countryofresidence and nationality are omitted — staticweb has no geo-ip
      // session data, and the sheet only passes them "if available".
      platform: toValue(isMobile) ? 'mobile' : 'desktop',
      pagetype: 'Property Details 3PI',
      staticpageflag: true,
      numberguests: guests,
      numbernights: Math.max(0, dateTime.getDiffInDays(checkIn, checkOut) || 0),
      applicationenvironment: runtimeConfig?.public?.RELEASE_ENV || 'Production',
      leadtime: Math.max(0, dateTime.getDiffInDays(dateTime.today, checkIn) || 0),
      propertypricefrom: property?.privateMinPrice?.value || property?.sharedMinPrice?.value || 0,
      category: property?.propertyCategory || property?.type || '',
      releaseversion: runtimeConfig?.public?.RELEASE_VERSION || '0.0.0',
      entrypage: channelAttribution?.entrypage || TRACKING_DEFAULT,
      dma: `${loadFunctional}|${loadAdvertising}|${loadAdvertising}|${loadAdvertising}`,
      locationid: property?.id || null,
      destinationcityid: locationInfo?.id || null,
      destinationcity: locationInfo?.cityEn || locationInfo?.city || '',
      destinationcountry: locationInfo?.countryEn || locationInfo?.country || '',
      translateddestinationcountry: locationInfo?.country || '',
      translateddestinationcity: locationInfo?.city || '',
      geotype: 'city',
      ref_source: channelAttribution?.source ?? TRACKING_DEFAULT,
      affiliate_id: channelAttribution?.affiliate ?? TRACKING_DEFAULT,
      googlecampaign: channelAttribution?.campaign_id ?? TRACKING_DEFAULT,
      referrer: channelAttribution?.referrer ?? TRACKING_DEFAULT,
    };

    if (property?.avgRating) {
      paramDict.displayableratingvalue = property.avgRating;
    }
    if (property?.numberReviews) {
      paramDict.numberofreviews = property.numberReviews;
    }

    return toBase64(JSON.stringify(paramDict));
  };

  const appendThreePiQueryParams = (baseUrl, property, locationInfo) => {
    if (!baseUrl) {
      return '';
    }

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('q', encodeThreePiParameters(property, locationInfo));
      return url.toString();
    } catch (err) {
      console.error('### Failed to append the 3PI q token:', err);
      return baseUrl;
    }
  };

  return { appendThreePiQueryParams };
}
